import React from 'react';
import { Link } from 'react-router-dom';
import PageTitle from '../components/ui/PageTitle';
import tileBreakerImg from '../assets/img-tile_breakers.png';
import vacuumImg from '../assets/img-hepa_vacuums.png';
import grinderImg from '../assets/img-grinders.png';
import debrisImg from '../assets/img-debris_collection.png';

const manuals = [
     {
          name: 'Tile Breaker',
          image: tileBreakerImg,
          file: '/manuals/Bosch_11264evs.pdf',
          filename: 'Bosch_11264evs.pdf',
          blurb:
               'Stand-up demolition hammer. Wear eye, ear, and respiratory protection — tile breakers kick up silica dust.',
     },
     {
          name: 'HEPA Vacuum',
          image: vacuumImg,
          file: '/manuals/Vacuum_S26.pdf',
          filename: 'Vacuum_S26.pdf',
          blurb:
               'Commercial vacuum with Longopac® bagging. Always empty bags outside, never in living space.',
     },
     {
          name: 'Floor Grinder',
          image: grinderImg,
          file: '/manuals/Metabo_W24-230_MVT.pdf',
          filename: 'Metabo_W24-230_MVT.pdf',
          blurb:
               'Hand-held grinder with diamond wheel. Two-hand operation only — never defeat the guard.',
     },
     {
          name: 'Edging Tool',
          image: debrisImg,
          file: '/manuals/Bosch_Bulldog_Extreme.pdf',
          filename: 'Bosch_Bulldog_Extreme.pdf',
          blurb:
               'Rotary hammer for tight corners and thinset. Keep the chisel locked before triggering.',
     },
];

const beforeYouStart = [
     {
          title: 'Read every manual',
          body: 'Each tool has a dedicated manual below. Spend 5 minutes before you start — it is the difference between a clean day and an ER visit.',
     },
     {
          title: 'Wear the right PPE',
          body: 'Eye protection, hearing protection, N95 respirator, and sturdy footwear. Silica dust is a long-term health risk even with HEPA equipment running.',
     },
     {
          title: 'Test before you commit',
          body: 'Do a short trial run in an inconspicuous corner. Confirm dust capture is working and you are comfortable with the tool before the main rip-out.',
     },
];

export default function Safety() {
     return (
          <>
               <PageTitle
                    firstWord='Equipment'
                    secondWord='Safety & Manuals'
                    breadcrumbLink='Safety'
               />
               <div className='mx-auto max-w-7xl px-6 py-12 laptop:py-24 laptop:px-8'>
                    <div className='laptop:grid laptop:grid-cols-12 laptop:gap-12'>
                         {/* Left rail — intro + contact */}
                         <div className='laptop:col-span-5'>
                              <div className='sticky top-32'>
                                   <h1 className='origin-left text-dark uppercase font-bold text-[40px] leading-[2.5rem] mb-3'>
                                        <span className='block text-[30px] text-yellow'>
                                             Knowledge &amp; Safety
                                        </span>
                                        Using The Equipment
                                   </h1>
                                   <p className='text-darker/70 mt-6 leading-relaxed'>
                                        Every tool we rent comes with a manufacturer's
                                        manual. Download them below and review before
                                        you start. Have questions?
                                   </p>
                                   <p className='mt-3'>
                                        <Link
                                             to='#'
                                             onClick={(e) => {
                                                  window.location.href =
                                                       'mailto:sales@ripoutrentals.com';
                                                  e.preventDefault();
                                             }}
                                             className='font-semibold text-yellow hover:underline'
                                        >
                                             sales@ripoutrentals.com
                                        </Link>
                                        <br />
                                        <a
                                             href='tel:6023624644'
                                             className='font-semibold text-darker'
                                        >
                                             602-362-4644
                                        </a>
                                   </p>
                              </div>
                         </div>

                         {/* Right column — content */}
                         <div className='mt-10 laptop:col-span-7 laptop:mt-0'>
                              {/* Before You Start callout */}
                              <div className='rounded-2xl border-2 border-yellow bg-yellow/5 p-6 mb-10'>
                                   <h2 className='text-dark uppercase font-bold text-xl tracking-wider mb-4'>
                                        Before You Start
                                   </h2>
                                   <ul className='space-y-4'>
                                        {beforeYouStart.map((tip, i) => (
                                             <li key={tip.title} className='flex items-start gap-3'>
                                                  <span className='flex-shrink-0 w-7 h-7 rounded-full bg-yellow text-dark font-bold flex items-center justify-center text-sm'>
                                                       {i + 1}
                                                  </span>
                                                  <div>
                                                       <p className='text-darker font-bold'>{tip.title}</p>
                                                       <p className='text-darker/70 text-sm leading-relaxed'>
                                                            {tip.body}
                                                       </p>
                                                  </div>
                                             </li>
                                        ))}
                                   </ul>
                              </div>

                              {/* Manuals grid */}
                              <h2 className='text-dark uppercase font-bold text-2xl laptop:text-[32px] leading-tight mb-6'>
                                   Tool Manuals
                              </h2>
                              <div className='grid grid-cols-1 tablet:grid-cols-2 gap-4'>
                                   {manuals.map((m) => (
                                        <ManualCard key={m.name} manual={m} />
                                   ))}
                              </div>
                         </div>
                    </div>
               </div>
          </>
     );
}

function ManualCard({ manual }) {
     return (
          <div className='rounded-2xl border border-gray-200 bg-white overflow-hidden flex flex-col hover:border-yellow duration-300'>
               <div className='w-full h-44 bg-white flex items-center justify-center border-b border-gray-200 p-4'>
                    <img
                         src={manual.image}
                         alt={manual.name}
                         className='w-full h-full object-contain'
                    />
               </div>
               <div className='p-5 flex flex-col flex-1'>
                    <h3 className='text-dark font-bold text-lg mb-1'>
                         {manual.name}
                    </h3>
                    <p className='text-darker/70 text-sm leading-relaxed flex-1 mb-4'>
                         {manual.blurb}
                    </p>
                    <a
                         href={manual.file}
                         download
                         className='group inline-flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-dark text-white font-semibold text-sm uppercase tracking-wider hover:bg-yellow hover:text-dark duration-300'
                    >
                         <DownloadIcon />
                         <span>Download Manual</span>
                         <span className='text-white/40 group-hover:text-dark/60 text-xs font-normal normal-case tracking-normal ml-1'>
                              ({manual.filename.split('.').pop().toUpperCase()})
                         </span>
                    </a>
               </div>
          </div>
     );
}

function DownloadIcon() {
     return (
          <svg
               xmlns='http://www.w3.org/2000/svg'
               width='16'
               height='16'
               viewBox='0 0 24 24'
               fill='none'
               stroke='currentColor'
               strokeWidth='2.5'
               strokeLinecap='round'
               strokeLinejoin='round'
               aria-hidden='true'
          >
               <path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' />
               <polyline points='7 10 12 15 17 10' />
               <line x1='12' y1='15' x2='12' y2='3' />
          </svg>
     );
}
